import {
  configureStore,
  ThunkAction,
  Action,
  MiddlewareAPI,
  isRejectedWithValue,
  isRejected,
  Middleware,
} from "@reduxjs/toolkit";
import authReducer from "./slices/authSlice";
import appReducer, { toggleLoading } from "./slices/appSlice";
import popupReducer from "./slices/popupSlice";
import clientLogoReducer from "./slices/clientLogoSlice";
import projectsReducer from "./slices/normalizedProjectsSlice";
import deliveryWizardReducer from "./slices/deliveryWizardSlice";
import productTourReducer from "./slices/productTourSlice";
import advertisersReducer from "./slices/advertisersSlice";
import brandsReducer from "./slices/brandsSlice";
import videoCheckerReducer from "./slices/videoCheckerSlice";
import servicePackageReducer from "./slices/servicePackageSlice";
import { jumboMediaAPI } from "../api/service";
import { toast } from "react-toastify";
import { setupListeners } from "@reduxjs/toolkit/query";

export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
    if (isRejectedWithValue(action) || isRejected(action)) {
      console.warn(action);
      const exceptions = ["AlreadyUploading", "False", "DOCTYPE"];
      const data = action?.payload?.data;

      if (data === "True") {
        return next(action);
      }

      if (data) {
        const msg = !exceptions.some((exp) => data.includes?.(exp))
          ? data
          : "Something went wrong";

        toast(msg, {
          type: "error",
        });
      }
    }
    return next(action);
  };

export const globalLoadingChecker: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    if (action.type === "jumboMediaAPI/executeMutation/pending") {
      api.dispatch(toggleLoading(true));
    }
    if (
      action.type === "jumboMediaAPI/executeMutation/fulfilled" ||
      action.type === "jumboMediaAPI/executeMutation/rejected"
    ) {
      api.dispatch(toggleLoading(false));
    }
    return next(action);
  };

const middlewares = [
  jumboMediaAPI.middleware,
  rtkQueryErrorLogger,
  globalLoadingChecker,
];
export const store = configureStore({
  reducer: {
    auth: authReducer,
    app: appReducer,
    popup: popupReducer,
    clientLogo: clientLogoReducer,
    normalizedProjects: projectsReducer,
    deliveryWizard: deliveryWizardReducer,
    productTour: productTourReducer,
    advertisers: advertisersReducer,
    brands: brandsReducer,
    videoChecker: videoCheckerReducer,
    servicePackage: servicePackageReducer,
    [jumboMediaAPI.reducerPath]: jumboMediaAPI.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(middlewares),
});

setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: {
      auth: authReducer,
      app: appReducer,
      popup: popupReducer,
      clientLogo: clientLogoReducer,
      normalizedProjects: projectsReducer,
      deliveryWizard: deliveryWizardReducer,
      productTour: productTourReducer,
      advertisers: advertisersReducer,
      brands: brandsReducer,
      videoChecker: videoCheckerReducer,
      servicePackage: servicePackageReducer,
      [jumboMediaAPI.reducerPath]: jumboMediaAPI.reducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(middlewares),
    preloadedState,
  });
};

export type AppStore = ReturnType<typeof setupStore>